import React, { useEffect, useState } from "react";
import Navbar from "../../../components/Navbar";
import API from "../../../services/api";

interface Category {
  _id: string;
  name: string;
  description?: string;
  image?: string;
  parent_id?: string | null;
  status?: "active" | "inactive";
  subcategories?: Category[];
}

interface CategoryForm {
  name: string;
  parent_id: string;
  description: string;
  status: "active" | "inactive";
  image: File | null;
}

const initialForm: CategoryForm = {
  name: "",
  parent_id: "",
  description: "",
  status: "active",
  image: null,
};

const Category: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);

  const [subcategories, setSubcategories] = useState<
    Record<string, Category[]>
  >({});

  const [expandedCategories, setExpandedCategories] = useState<
    Record<string, boolean>
  >({});

  const [loading, setLoading] = useState(true);

  const [loadingSubcategories, setLoadingSubcategories] = useState<
    Record<string, boolean>
  >({});

  const [showModal, setShowModal] = useState(false);

  const [saving, setSaving] = useState(false);

  const [editingCategory, setEditingCategory] =
    useState<Category | null>(null);

  const [form, setForm] =
    useState<CategoryForm>(initialForm);

  const [imagePreview, setImagePreview] =
    useState<string>("");

  /* =========================================================
     GET CATEGORIES
  ========================================================= */

  const fetchCategories = async () => {
    try {
      setLoading(true);

      const response = await API.get("/categories");

      const result = response.data;

      const data = Array.isArray(result.data)
        ? result.data
        : result.data?.categories || [];

      setCategories(data);
    } catch (error) {
      console.error(
        "Fetch categories error:",
        error
      );

      alert("Unable to load categories");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  /* =========================================================
     GET SUBCATEGORIES
  ========================================================= */

  const fetchSubcategories = async (
    categoryId: string
  ) => {
    try {
      setLoadingSubcategories((prev) => ({
        ...prev,
        [categoryId]: true,
      }));

      const response = await API.get(
        `/categories/${categoryId}/subcategories`
      );

      const result = response.data;

      const data = Array.isArray(result.data)
        ? result.data
        : result.data?.subcategories || [];

      setSubcategories((prev) => ({
        ...prev,
        [categoryId]: data,
      }));
    } catch (error) {
      console.error(
        "Fetch subcategories error:",
        error
      );

      alert("Unable to load subcategories");
    } finally {
      setLoadingSubcategories((prev) => ({
        ...prev,
        [categoryId]: false,
      }));
    }
  };

  /* =========================================================
     EXPAND / COLLAPSE
  ========================================================= */

  const toggleCategory = async (
    categoryId: string
  ) => {
    const isExpanded =
      expandedCategories[categoryId];

    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !isExpanded,
    }));

    if (
      !isExpanded &&
      !subcategories[categoryId]
    ) {
      await fetchSubcategories(categoryId);
    }
  };

  /* =========================================================
     OPEN ADD CATEGORY
  ========================================================= */

  const handleAddCategory = () => {
    setEditingCategory(null);

    setForm({
      ...initialForm,
      parent_id: "",
    });

    setImagePreview("");

    setShowModal(true);
  };

  /* =========================================================
     OPEN ADD SUBCATEGORY
  ========================================================= */

  const handleAddSubcategory = (
    category: Category
  ) => {
    setEditingCategory(null);

    setForm({
      ...initialForm,
      parent_id: category._id,
    });

    setImagePreview("");

    setShowModal(true);
  };

  /* =========================================================
     OPEN EDIT
  ========================================================= */

  const handleEdit = (
    category: Category
  ) => {
    setEditingCategory(category);

    setForm({
      name: category.name || "",
      parent_id:
        category.parent_id || "",
      description:
        category.description || "",
      status:
        category.status || "active",
      image: null,
    });

    setImagePreview(
      category.image || ""
    );

    setShowModal(true);
  };

  /* =========================================================
     IMAGE CHANGE
  ========================================================= */

  const handleImageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file =
      event.target.files?.[0];

    if (!file) return;

    setForm((prev) => ({
      ...prev,
      image: file,
    }));

    setImagePreview(
      URL.createObjectURL(file)
    );
  };

  /* =========================================================
     CLOSE MODAL
  ========================================================= */

  const closeModal = () => {
    if (saving) return;

    setShowModal(false);

    setEditingCategory(null);

    setForm(initialForm);

    setImagePreview("");
  };

  /* =========================================================
     ADD / UPDATE CATEGORY
  ========================================================= */

  const handleSubmit = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    if (!form.name.trim()) {
      alert(
        "Category name is required"
      );

      return;
    }

    /*
     * Prevent a category from becoming
     * its own parent.
     */
    if (
      editingCategory &&
      form.parent_id ===
        editingCategory._id
    ) {
      alert(
        "A category cannot be its own parent."
      );

      return;
    }

    /*
     * Keep old parent so we can
     * refresh it after moving
     * a subcategory.
     */
    const oldParentId =
      editingCategory?.parent_id ||
      "";

    const newParentId =
      form.parent_id;

    try {
      setSaving(true);

      const formData =
        new FormData();

      formData.append(
        "name",
        form.name.trim()
      );

      /*
       * Main category:
       * parent_id = empty
       *
       * Subcategory:
       * parent_id = category ID
       */
      formData.append(
        "parent_id",
        form.parent_id || ""
      );

      formData.append(
        "description",
        form.description
      );

      if (editingCategory) {
        formData.append(
          "status",
          form.status
        );
      }

      if (form.image) {
        formData.append(
          "image",
          form.image
        );
      }

      const url =
        editingCategory
          ? `https://www.frendrops.in/api/category/${editingCategory._id}`
          : `https://www.frendrops.in/api/add-category`;

      const response =
        await fetch(url, {
          method:
            editingCategory
              ? "PUT"
              : "POST",

          body: formData,
        });

      const result =
        await response.json();

      if (
        !response.ok ||
        !result.success
      ) {
        throw new Error(
          result.message ||
            "Failed to save category"
        );
      }

      /*
       * Close modal first.
       */
      setShowModal(false);

      setEditingCategory(null);

      setForm(initialForm);

      setImagePreview("");

      /*
       * Refresh main category list.
       */
      await fetchCategories();

      /*
       * Refresh OLD parent when
       * moving a subcategory.
       */
      if (oldParentId) {
        await fetchSubcategories(
          oldParentId
        );
      }

      /*
       * Refresh NEW parent.
       */
      if (
        newParentId &&
        newParentId !==
          oldParentId
      ) {
        await fetchSubcategories(
          newParentId
        );
      }

      /*
       * Automatically expand the
       * selected new parent.
       */
      if (newParentId) {
        setExpandedCategories(
          (prev) => ({
            ...prev,
            [newParentId]: true,
          })
        );
      }
    } catch (error) {
      console.error(
        "Save category error:",
        error
      );

      alert(
        error instanceof Error
          ? error.message
          : "Something went wrong"
      );
    } finally {
      setSaving(false);
    }
  };

  /* =========================================================
     CHANGE STATUS
  ========================================================= */

  const handleStatusChange = async (
    category: Category,
    status:
      | "active"
      | "inactive"
  ) => {
    try {
      const response =
        await fetch(
          `https://www.frendrops.in/api/category/${category._id}/status`,
          {
            method: "PATCH",

            headers: {
              "Content-Type":
                "application/json",
            },

            body: JSON.stringify({
              status,
            }),
          }
        );

      const result =
        await response.json();

      if (
        !response.ok ||
        !result.success
      ) {
        throw new Error(
          result.message ||
            "Failed to update status"
        );
      }

      /*
       * Update main categories.
       */
      setCategories((prev) =>
        prev.map((item) =>
          item._id ===
          category._id
            ? {
                ...item,
                status,
              }
            : item
        )
      );

      /*
       * Update loaded subcategories.
       */
      setSubcategories((prev) => {
        const updated = {
          ...prev,
        };

        Object.keys(
          updated
        ).forEach(
          (parentId) => {
            updated[parentId] =
              updated[parentId].map(
                (item) =>
                  item._id ===
                  category._id
                    ? {
                        ...item,
                        status,
                      }
                    : item
              );
          }
        );

        return updated;
      });
    } catch (error) {
      console.error(
        "Status update error:",
        error
      );

      alert(
        error instanceof Error
          ? error.message
          : "Unable to update status"
      );
    }
  };

  /* =========================================================
     DELETE
  ========================================================= */

  const handleDelete = async (
    category: Category
  ) => {
    const confirmed =
      window.confirm(
        `Are you sure you want to delete "${category.name}"?`
      );

    if (!confirmed) return;

    try {
      const response =
        await fetch(
          `https://www.frendrops.in/api/category/${category._id}`,
          {
            method: "DELETE",
          }
        );

      const result =
        await response.json();

      if (
        !response.ok ||
        !result.success
      ) {
        throw new Error(
          result.message ||
            "Failed to delete category"
        );
      }

      /*
       * Remove main category.
       */
      setCategories((prev) =>
        prev.filter(
          (item) =>
            item._id !==
            category._id
        )
      );

      /*
       * Remove subcategory.
       */
      setSubcategories((prev) => {
        const updated = {
          ...prev,
        };

        Object.keys(
          updated
        ).forEach(
          (parentId) => {
            updated[parentId] =
              updated[
                parentId
              ].filter(
                (item) =>
                  item._id !==
                  category._id
              );
          }
        );

        return updated;
      });

      alert(
        "Deleted successfully"
      );
    } catch (error) {
      console.error(
        "Delete error:",
        error
      );

      alert(
        error instanceof Error
          ? error.message
          : "Unable to delete category"
      );
    }
  };

  /* =========================================================
     STATUS BADGE
  ========================================================= */

  const StatusBadge = ({
    category,
  }: {
    category: Category;
  }) => {
    const status =
      category.status || "active";

    return (
      <select
        value={status}
        onChange={(e) =>
          handleStatusChange(
            category,
            e.target.value as
              | "active"
              | "inactive"
          )
        }
        className={`rounded-full border px-3 py-1 text-xs font-medium outline-none ${
          status === "active"
            ? "border-green-200 bg-green-50 text-green-700"
            : "border-red-200 bg-red-50 text-red-600"
        }`}
      >
        <option value="active">
          Active
        </option>

        <option value="inactive">
          Inactive
        </option>
      </select>
    );
  };

  /* =========================================================
     CATEGORY ROW
  ========================================================= */

  const CategoryRow = ({
    category,
    isSubcategory = false,
  }: {
    category: Category;
    isSubcategory?: boolean;
    parentCategoryId?: string;
  }) => {
    const isExpanded =
      expandedCategories[
        category._id
      ];

    return (
      <>
        <tr className="border-b border-gray-100 hover:bg-gray-50">
          {/* Category */}
          <td className="px-4 py-4">
            <div
              className={`flex items-center gap-3 ${
                isSubcategory
                  ? "pl-8"
                  : ""
              }`}
            >
              {!isSubcategory && (
                <button
                  type="button"
                  onClick={() =>
                    toggleCategory(
                      category._id
                    )
                  }
                  className="flex h-7 w-7 items-center justify-center rounded-md text-gray-500 hover:bg-gray-200"
                  title={
                    isExpanded
                      ? "Hide subcategories"
                      : "Show subcategories"
                  }
                >
                  {loadingSubcategories[
                    category._id
                  ] ? (
                    <span className="h-3 w-3 animate-spin rounded-full border-2 border-gray-400 border-t-transparent" />
                  ) : (
                    <span
                      className={`text-xs transition-transform ${
                        isExpanded
                          ? "rotate-90"
                          : ""
                      }`}
                    >
                      ▶
                    </span>
                  )}
                </button>
              )}

              {isSubcategory && (
                <span className="text-gray-400">
                  ↳
                </span>
              )}

              {/* Image */}
              <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg border border-gray-100 bg-gray-100">
                {category.image ? (
                  <img
                    src={
                      category.image
                    }
                    alt={
                      category.name
                    }
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-lg">
                    🌸
                  </div>
                )}
              </div>

              <div className="min-w-0">
                <p className="truncate font-medium text-gray-900">
                  {category.name}
                </p>

                {category.description && (
                  <p className="mt-0.5 max-w-[280px] truncate text-xs text-gray-500">
                    {
                      category.description
                    }
                  </p>
                )}
              </div>
            </div>
          </td>

          {/* Type */}
          <td className="px-4 py-4">
            <span className="text-sm text-gray-600">
              {isSubcategory
                ? "Subcategory"
                : "Category"}
            </span>
          </td>

          {/* Status */}
          <td className="px-4 py-4">
            <StatusBadge
              category={
                category
              }
            />
          </td>

          {/* Actions */}
          <td className="px-4 py-4">
            <div className="flex flex-wrap items-center gap-2">
              {!isSubcategory && (
                <button
                  type="button"
                  onClick={() =>
                    handleAddSubcategory(
                      category
                    )
                  }
                  className="rounded-md border border-purple-200 px-3 py-1.5 text-xs font-medium text-purple-600 hover:bg-purple-50"
                >
                  + Subcategory
                </button>
              )}

              <button
                type="button"
                onClick={() =>
                  handleEdit(
                    category
                  )
                }
                className="rounded-md border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100"
              >
                Edit
              </button>

              <button
                type="button"
                onClick={() =>
                  handleDelete(
                    category
                  )
                }
                className="rounded-md border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
              >
                Delete
              </button>
            </div>
          </td>
        </tr>

        {/* Subcategories */}
        {!isSubcategory &&
          isExpanded &&
          subcategories[
            category._id
          ]?.map(
            (
              subcategory
            ) => (
              <CategoryRow
                key={
                  subcategory._id
                }
                category={
                  subcategory
                }
                isSubcategory
                parentCategoryId={
                  category._id
                }
              />
            )
          )}
      </>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main className="px-4 py-5 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-[1600px]">
          {/* Header */}

          <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-lg font-medium text-gray-900">
                Categories
              </h1>

              <p className="mt-1 text-sm text-gray-500">
                Manage categories and
                subcategories
              </p>
            </div>

            <button
              type="button"
              onClick={
                handleAddCategory
              }
              className="rounded-md bg-[#6555E8] px-4 py-2 text-sm font-medium text-white transition hover:bg-[#5747D5]"
            >
              + Add Category
            </button>
          </div>

          {/* =====================================================
              TABLE
          ===================================================== */}

          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px]">
                <thead className="border-b border-gray-200 bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                      Category
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                      Type
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                      Status
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td
                        colSpan={4}
                        className="px-4 py-12 text-center"
                      >
                        <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                          <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-[#6555E8]" />

                          Loading
                          categories...
                        </div>
                      </td>
                    </tr>
                  ) : categories.length ===
                    0 ? (
                    <tr>
                      <td
                        colSpan={4}
                        className="px-4 py-14 text-center"
                      >
                        <div className="text-3xl">
                          🌸
                        </div>

                        <p className="mt-3 text-sm font-medium text-gray-800">
                          No categories
                          found
                        </p>

                        <p className="mt-1 text-xs text-gray-500">
                          Add your first
                          category to get
                          started.
                        </p>

                        <button
                          type="button"
                          onClick={
                            handleAddCategory
                          }
                          className="mt-4 rounded-md bg-[#6555E8] px-4 py-2 text-sm text-white"
                        >
                          + Add
                          Category
                        </button>
                      </td>
                    </tr>
                  ) : (
                    categories.map(
                      (category) => (
                        <CategoryRow
                          key={
                            category._id
                          }
                          category={
                            category
                          }
                        />
                      )
                    )
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <p className="mt-3 text-xs text-gray-400 sm:hidden">
            Swipe left/right to
            view all category
            options.
          </p>
        </div>
      </main>

      {/* =====================================================
          ADD / EDIT MODAL
      ===================================================== */}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white shadow-2xl">
            {/* Modal Header */}

            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-5 py-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {editingCategory
                    ? "Edit Category"
                    : form.parent_id
                    ? "Add Subcategory"
                    : "Add Category"}
                </h2>

                <p className="mt-0.5 text-xs text-gray-500">
                  {editingCategory
                    ? "Update category details and parent category"
                    : form.parent_id
                    ? "Create a subcategory under the selected category"
                    : "Create a new main category"}
                </p>
              </div>

              <button
                type="button"
                onClick={
                  closeModal
                }
                disabled={saving}
                className="flex h-8 w-8 items-center justify-center rounded-md text-xl text-gray-400 hover:bg-gray-100 hover:text-gray-700"
              >
                ×
              </button>
            </div>

            {/* Form */}

            <form
              onSubmit={
                handleSubmit
              }
              className="space-y-5 p-5"
            >
              {/* Name */}

              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">
                  Name

                  <span className="ml-1 text-red-500">
                    *
                  </span>
                </label>

                <input
                  type="text"
                  value={form.name}
                  onChange={(e) =>
                    setForm(
                      (prev) => ({
                        ...prev,
                        name:
                          e.target
                            .value,
                      })
                    )
                  }
                  placeholder="Enter category name"
                  className="w-full rounded-md border border-gray-300 px-3 py-2.5 text-sm outline-none transition focus:border-[#6555E8] focus:ring-2 focus:ring-purple-100"
                  required
                />
              </div>

              {/* =================================================
                  PARENT CATEGORY - NOW EDITABLE
              ================================================= */}

              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">
                  Parent Category
                </label>

                <select
                  value={
                    form.parent_id
                  }
                  onChange={(e) =>
                    setForm(
                      (prev) => ({
                        ...prev,
                        parent_id:
                          e.target
                            .value,
                      })
                    )
                  }
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-[#6555E8] focus:ring-2 focus:ring-purple-100"
                >
                  <option value="">
                    None — Main
                    Category
                  </option>

                  {categories
                    .filter(
                      (
                        category
                      ) =>
                        category._id !==
                        editingCategory?._id
                    )
                    .map(
                      (
                        category
                      ) => (
                        <option
                          key={
                            category._id
                          }
                          value={
                            category._id
                          }
                        >
                          {
                            category.name
                          }
                        </option>
                      )
                    )}
                </select>

                <p className="mt-1 text-xs text-gray-400">
                  Choose a parent
                  category to make
                  this a subcategory.
                  Select "None" to
                  make it a main
                  category.
                </p>
              </div>

              {/* Description */}

              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">
                  Description
                </label>

                <textarea
                  value={
                    form.description
                  }
                  onChange={(e) =>
                    setForm(
                      (prev) => ({
                        ...prev,
                        description:
                          e.target
                            .value,
                      })
                    )
                  }
                  rows={3}
                  placeholder="Enter category description"
                  className="w-full resize-none rounded-md border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-[#6555E8] focus:ring-2 focus:ring-purple-100"
                />
              </div>

              {/* Status */}

              {editingCategory && (
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">
                    Status
                  </label>

                  <select
                    value={
                      form.status
                    }
                    onChange={(e) =>
                      setForm(
                        (prev) => ({
                          ...prev,
                          status:
                            e.target
                              .value as
                              | "active"
                              | "inactive",
                        })
                      )
                    }
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#6555E8]"
                  >
                    <option value="active">
                      Active
                    </option>

                    <option value="inactive">
                      Inactive
                    </option>
                  </select>
                </div>
              )}

              {/* Image */}

              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">
                  Image
                </label>

                {imagePreview && (
                  <div className="relative mb-3 h-32 w-32 overflow-hidden rounded-lg border border-gray-200">
                    <img
                      src={
                        imagePreview
                      }
                      alt="Preview"
                      className="h-full w-full object-cover"
                    />

                    <button
                      type="button"
                      onClick={() => {
                        setImagePreview(
                          ""
                        );

                        setForm(
                          (prev) => ({
                            ...prev,
                            image: null,
                          })
                        );
                      }}
                      className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-sm text-white"
                    >
                      ×
                    </button>
                  </div>
                )}

                <label className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 px-4 py-6 transition hover:border-[#6555E8] hover:bg-purple-50">
                  <span className="text-2xl">
                    📷
                  </span>

                  <span className="mt-2 text-sm font-medium text-gray-700">
                    Choose image
                  </span>

                  <span className="mt-1 text-xs text-gray-400">
                    PNG, JPG or
                    WEBP
                  </span>

                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={
                      handleImageChange
                    }
                    className="hidden"
                  />
                </label>
              </div>

              {/* Buttons */}

              <div className="flex gap-3 border-t border-gray-200 pt-5">
                <button
                  type="button"
                  onClick={
                    closeModal
                  }
                  disabled={saving}
                  className="flex-1 rounded-md border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 rounded-md bg-[#6555E8] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#5747D5] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {saving ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />

                      Saving...
                    </span>
                  ) : editingCategory ? (
                    "Update Category"
                  ) : form.parent_id ? (
                    "Add Subcategory"
                  ) : (
                    "Add Category"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Category;
